/* eslint-disable react/jsx-props-no-spreading */
import { postJSON } from '@folklore/fetch';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import useElementSize from '../../hooks/useElementSize';

import styles from '../../styles/pages/playground-page.module.css';

const instructions = `
Tu es un générateur d'animation javascript en utilisant le canvas 2D.
À partir de commandes textuelles, tu retourner une variable layers qui contient différente couche. Chaque couche est un objet ayant la forme suivante:

{
    name: 'Nom de la couche', // Nom de la couche
    draw: function (context, width,height, time) { // Fonction pour dessiner la couche

    }
}

La fonction pour dessiner la couche a la signature suivante:

function (context, width,height, delta, state = null) {
    return state;
}

Les arguments sont:
- context: le contexte 2D du canvas
- width: la largeur du canvas
- height: la hauteur du canvas
- delta: le temps écoulé en millisecondes depuis le dernier tick (la dernière fois que la fonction a été appelée)
- state: Valeur de retour de la dernière fois où la fonction a été appelée, ou null si c'est la première fois. Peut être utilisé pour maintenir un état entre chaque tick.

La fonction doit dessiner ce qui est demandé en prenant soin de l'animer et d'utiliser la variable delta pour avoir une vitesse constante. Elle ne doit pas effacer le context.

Les commandes peuvent ajouter des couches en demandant de dessiner quelque chose. Par exemple:

- Ajoute un cercle bleu qui s'agrandi et se rapetisse en boucle.

Mais peuvent aussi modifier les layers en les enlevant. Par exemple:

- Enlève le cercle bleu

Tu dois seulement retourner le code javascript, sans texte et sans markdown. Sous la forme:
[
// Layers
];
`;

interface PlaygroundPageProps {
    current?: boolean;
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: () => void;
    onLeave?: () => void;
}

function PlaygroundPage({
    entered = false,
    entering = false,
    leaving = false,
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: PlaygroundPageProps): JSX.Element | null {
    const { ref: containerRef, width, height } = useElementSize();
    const [layers, setLayers] = useState([]);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const stateRef = useRef({});

    useEffect(() => {
        let canceled = false;
        let animation = null;
        let lastTime = Date.now();
        const ctx = canvasRef.current.getContext('2d');
        function tick() {
            if (canceled) {
                return;
            }
            const elapsed = Date.now() - lastTime;
            if (ctx) {
                ctx.clearRect(0, 0, width, height);
                layers.forEach((layer) => {
                    const newState = layer.draw(
                        ctx,
                        width,
                        height,
                        elapsed,
                        stateRef[layer.name] || null,
                    );
                    stateRef[layer.name] = newState;
                });
            }
            lastTime = Date.now();
            animation = requestAnimationFrame(tick);
        }
        tick();

        return () => {
            canceled = true;
            cancelAnimationFrame(animation);
        };
    }, [layers]);

    const previousResponseIdRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [input, setInput] = useState('');
    const [command, setCommand] = useState(
        'Ajoute un cercle bleu rouge qui passe de 20% de la taille du canvas à 80%, en boucle.',
    );
    const onInputChange = useCallback(
        (event) => {
            setInput(event.target.value);
        },
        [setInput],
    );
    const onFormSubmit = useCallback(
        (event) => {
            event.preventDefault();
            setCommand(input);
            setInput('');
        },
        [input, setCommand],
    );
    useEffect(() => {
        setLoading(true);
        postJSON(
            'https://api.openai.com/v1/responses',
            {
                model: 'gpt-4.1',
                instructions,
                input: command,
                ...(previousResponseIdRef.current !== null
                    ? { previous_response_id: previousResponseIdRef.current }
                    : {}),
            },
            {
                headers: {
                    Authorization: `Bearer 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`,
                },
            },
        ).then((response) => {
            setLoading(false);
            const { output, id } = response;
            const [
                {
                    content: [{ text }],
                },
            ] = output || [];
            previousResponseIdRef.current = id;
            console.log(text);
            const newLayers = eval(text);
            console.log(newLayers);
            setLayers(newLayers);
        });
    }, [command]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.entering]: entering,
                    [styles.leaving]: leaving,
                },
                className,
                contentClassName,
            ])}
            data-brand="urbania"
            data-theme="dark"
            ref={containerRef}
        >
            <form onSubmit={onFormSubmit} className={styles.form}>
                <input
                    type="text"
                    value={input}
                    className={styles.input}
                    onChange={onInputChange}
                />
                <button type="submit" disabled={loading}>
                    {loading ? 'Chargement...' : 'Envoyer'}
                </button>
            </form>
            <canvas width={width} height={height} ref={canvasRef} className={styles.canvas} />
        </div>
    );
}

export default PlaygroundPage;
